import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  Heading,
  Badge,
  Modal,
  TextContainer,
  List,
  Link,
  Frame,
  Icon,
  Stack
} from '@shopify/polaris';
import { CircleTickMajor, StarFilledMinor } from '@shopify/polaris-icons';
import LandingNav from './components/LandingNav';

// Add CSS styles for mobile responsiveness
const mobileStyles = `
  @media (max-width: 768px) {
    .desktop-benefits {
      display: none !important;
    }
    .mobile-benefits-slider {
      display: block !important;
    }
    .desktop-trust-cards {
      display: none !important;
    }
    .mobile-trust-slider {
      display: block !important;
    }
    .desktop-template-cards {
      display: none !important;
    }
    .mobile-template-slider {
      display: block !important;
    }
  }
`;

// Testimonial Slider Component
const TestimonialSlider = () => {
  // Testimonial data
  const testimonials = [
    {
      name: 'Sarah',
      company: 'LuxeWear Boutique',
      avatar: { letter: 'S', color: '#008060' },
      quote: 'Saved me 15+ hours a week. Now I can focus on selling.',
      rating: 5,
      tag: { text: 'Apparel Brand', emoji: '👗', status: 'success' }
    },
    {
      name: 'Tom',
      company: 'GadgetCrate',
      avatar: { letter: 'T', color: '#4f46e5' },
      quote: 'Love the bulk editor. The AI descriptions are 🤩',
      rating: 4,
      tag: { text: 'Electronics', emoji: '📱' }
    },
    {
      name: 'Jessica',
      company: 'Outdoor Explorers',
      avatar: { letter: 'J', color: '#e05d37' },
      quote: 'The AI template system is a game-changer for our product listings.',
      rating: 5,
      tag: { text: 'Outdoor Gear', emoji: '🏞️' }
    },
    {
      name: 'Michael',
      company: 'KitchenPro',
      avatar: { letter: 'M', color: '#2563eb' },
      quote: 'Finally a tool that makes bulk editing painless. Worth every penny!',
      rating: 5,
      tag: { text: 'Home Goods', emoji: '🏠' }
    },
    {
      name: 'Emma',
      company: 'BeautyBox',
      avatar: { letter: 'E', color: '#d946ef' },
      quote: 'Updating hundreds of cosmetic products used to take days. Now it takes hours.',
      rating: 4,
      tag: { text: 'Beauty Products', emoji: '💄' }
    },
    {
      name: 'David',
      company: 'FitnessFusion',
      avatar: { letter: 'D', color: '#16a34a' },
      quote: 'The price tools saved us during our seasonal sales. Updating 500+ products in minutes!',
      rating: 5,
      tag: { text: 'Fitness Equipment', emoji: '💪', status: 'success' }
    },
    {
      name: 'Rachel',
      company: 'BookHaven',
      avatar: { letter: 'R', color: '#9333ea' },
      quote: 'Managing our book inventory was a nightmare before. Now it\'s actually enjoyable.',
      rating: 5,
      tag: { text: 'Bookstore', emoji: '📚' }
    },
    {
      name: 'Alex',
      company: 'PetParadise',
      avatar: { letter: 'A', color: '#f97316' },
      quote: 'The AI templates understand our pet product descriptions perfectly. Customers love the detail!',
      rating: 4,
      tag: { text: 'Pet Supplies', emoji: '🐾' }
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Number of cards to show based on screen size
  const cardsToShow = isMobile ? 1 : 2;
  const maxIndex = Math.max(0, testimonials.length - cardsToShow);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle next/prev navigation
  const goToNext = useCallback(() => {
    if (currentIndex < maxIndex && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex(prev => prev + 1);
      setTimeout(() => setIsTransitioning(false), 500); // Match transition duration
    }
  }, [currentIndex, maxIndex, isTransitioning]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setCurrentIndex(prev => prev - 1);
      setTimeout(() => setIsTransitioning(false), 500); // Match transition duration
    }
  }, [currentIndex, isTransitioning]);

  // Auto-advance the slider
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentIndex < maxIndex) {
        goToNext();
      } else {
        setCurrentIndex(0); // Loop back to start
      }
    }, 6000); // Change every 6 seconds

    return () => clearTimeout(timer);
  }, [currentIndex, goToNext, maxIndex]);

  // Render stars for rating
  const renderStars = (rating) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ color: i < rating ? '#f59e0b' : '#d1d5db', marginRight: '2px' }}>
            ★
          </div>
        ))}
      </div>
    );
  };

  // Render a testimonial card
  const renderTestimonialCard = (testimonial) => {
    return (
      <div
        style={{
          background: 'white',
          padding: '30px',
          borderRadius: '10px',
          boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
          height: '100%',
          transition: 'all 0.3s ease',
          position: 'relative',
          overflow: 'hidden'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px) scale(1.02)';
          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 128, 96, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.05)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: testimonial.avatar.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '15px',
              fontSize: '24px',
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {testimonial.avatar.letter}
          </div>
          <div>
            <Text variant="headingMd">{testimonial.name}</Text>
            <Text variant="bodySm" color="subdued" style={{ color: '#6b7280' }}>
              {testimonial.company}
            </Text>
            <div style={{ marginTop: '5px' }}>
              {renderStars(testimonial.rating)}
            </div>
          </div>
        </div>
        <div>
          <Text variant="bodyMd" style={{ fontStyle: 'italic', fontSize: '16px', position: 'relative', zIndex: '1' }}>
            <span style={{ display: 'inline-block', marginRight: '5px', fontSize: '20px', color: '#008060' }}>💬</span> "{testimonial.quote}"
          </Text>
          <div style={{ marginTop: '15px' }}>
            <Badge status={testimonial.tag.status}>{testimonial.tag.emoji} {testimonial.tag.text}</Badge>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          width: 'auto',
          transition: 'transform 0.5s ease',
          transform: `translateX(-${currentIndex * 100 / cardsToShow}%)` // Simplified calculation
        }}
      >
        {testimonials.map((testimonial, index) => (
          <div key={index} style={{ width: `${100 / cardsToShow}%`, padding: '0 15px', boxSizing: 'border-box', flex: '0 0 auto' }}>
            {renderTestimonialCard(testimonial)}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
        <Button
          icon={<span style={{ transform: 'rotate(180deg)', display: 'inline-block' }}>→</span>}
          onClick={goToPrev}
          disabled={currentIndex === 0}
          outline
        >
          Previous
        </Button>
        <div style={{ width: '20px' }} />
        <Button
          icon={<span style={{ marginLeft: '5px' }}>→</span>}
          onClick={goToNext}
          disabled={currentIndex === maxIndex}
          outline
          iconPosition="right"
        >
          Next
        </Button>
      </div>

      {/* Dots Indicator */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '15px' }}>
        {[...Array(maxIndex + 1)].map((_, i) => (
          <div
            key={i}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: i === currentIndex ? '#008060' : '#d1d5db',
              margin: '0 5px',
              cursor: 'pointer',
              transition: 'background 0.3s ease'
            }}
            onClick={() => setCurrentIndex(i)}
          />
        ))}
      </div>
    </div>
  );
}

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTrustSlide, setCurrentTrustSlide] = useState(0);
  const [currentTemplateSlide, setCurrentTemplateSlide] = useState(0);
  
  // Add the mobile styles to the document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = mobileStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  const navigate = useNavigate();
  const [priceToolsModalOpen, setPriceToolsModalOpen] = useState(false);
  const [inventoryToolsModalOpen, setInventoryToolsModalOpen] = useState(false);
  const [productToolsModalOpen, setProductToolsModalOpen] = useState(false);

  const handleLoginClick = () => {
    navigate('/auth');
  };

  const handleNavClick = (path) => {
    navigate(path);
  };

  return (
    <Frame>
      <Page>
        <LandingNav currentPath="/" onNavClick={handleNavClick} />
        
        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #f5f9ff 0%, #eefbf4 100%)',
          padding: '60px 20px',
          borderRadius: '8px',
          marginBottom: '40px',
          textAlign: 'center',
          animation: 'fadeIn 1s ease-in-out'
        }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Heading element="h1">
              <span style={{ fontSize: '2.5rem', fontWeight: '700', color: '#212B36' }}>
              Effortlessly Edit & Scale Your Shopify Products
              </span>
            </Heading>
            <div style={{ margin: '20px 0 30px', fontSize: '1.25rem', color: '#637381' }}>
              <p>Save hours each week with powerful AI tools that bulk edit thousands of products — in minutes, not days.</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <Button size="large" primary onClick={handleLoginClick}>Try for Free</Button>
              <Button size="large" onClick={() => handleNavClick('/ai-engines')}>Explore AI Engines</Button>
            </div>
          </div>
        </div>

        {/* Key Benefits Section */}
        <div style={{ textAlign: 'center', margin: '50px 0 60px' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>✨</div>
          <Heading>Key Benefits</Heading>
          <Text variant="bodyLg" as="p" color="subdued" style={{ maxWidth: '700px', margin: '15px auto 60px' }}>
            Powerful tools to streamline your Shopify product management workflow
          </Text>
          
          {/* Desktop view - only shown on larger screens */}
          <div className="desktop-benefits" style={{ 
            display: 'flex', 
            justifyContent: 'center',
            gap: '20px',
            maxWidth: '850px',
            margin: '50px auto 0'
          }}>
          
            <div style={{ width: '260px' }}>
              <div style={{ 
                background: '#ffffff', 
                borderRadius: '10px', 
                padding: '35px 20px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>🧰</div>
                <Text variant="headingSm" as="h3">Bulk Edit Tools</Text>
                <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                  Update prices, inventory & product details in batches
                </Text>
              </div>
            </div>
            
            <div style={{ width: '260px' }}>
              <div style={{ 
                background: '#ffffff', 
                borderRadius: '10px', 
                padding: '35px 20px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>✍️</div>
                <Text variant="headingSm" as="h3">AI Content Generation</Text>
                <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                  Create product descriptions with customizable templates
                </Text>
              </div>
            </div>
            
            <div style={{ width: '260px' }}>
              <div style={{ 
                background: '#ffffff', 
                borderRadius: '10px', 
                padding: '35px 20px', 
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '32px', marginBottom: '16px' }}>📸</div>
                <Text variant="headingSm" as="h3">Image Analysis</Text>
                <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                  Extract product details from images with vision models
                </Text>
              </div>
            </div>
          </div>

          {/* Mobile slider - hidden by default, shown on mobile */}
          <div className="mobile-benefits-slider" style={{
            display: 'none',
            position: 'relative',
            maxWidth: '280px',
            margin: '30px auto 0'
          }}>
            <div style={{
              overflow: 'hidden',
              position: 'relative',
              borderRadius: '10px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div className="slider-container" style={{
                display: 'flex',
                transition: 'transform 0.3s ease-in-out',
                transform: `translateX(-${currentSlide * 100}%)`
              }}>
                {/* Slide 1 */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px' }}>🧰</div>
                  <Text variant="headingSm" as="h3">Bulk Edit Tools</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Update prices, inventory & product details in batches
                  </Text>
                </div>
                
                {/* Slide 2 */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px' }}>✍️</div>
                  <Text variant="headingSm" as="h3">AI Content Generation</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Create product descriptions with customizable templates
                  </Text>
                </div>
                
                {/* Slide 3 */}
                <div style={{
                  minWidth: '100%',
                  background: '#ffffff',
                  padding: '35px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '16px' }}>📸</div>
                  <Text variant="headingSm" as="h3">Image Analysis</Text>
                  <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                    Extract product details from images with vision models
                  </Text>
                </div>
              </div>
            </div>
            
            {/* Slider dots */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '15px',
              gap: '8px'
            }}>
              {[0, 1, 2].map((index) => (
                <button 
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: currentSlide === index ? '#008060' : '#e1e1e1',
                    cursor: 'pointer',
                    padding: 0
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
            
            {/* Left/Right navigation arrows */}
            <button 
              onClick={() => setCurrentSlide(prev => (prev === 0 ? 2 : prev - 1))}
              style={{
                position: 'absolute',
                left: '-15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                border: '1px solid #e1e1e1',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
              aria-label="Previous slide"
            >
              ‹
            </button>
            <button 
              onClick={() => setCurrentSlide(prev => (prev === 2 ? 0 : prev + 1))}
              style={{
                position: 'absolute',
                right: '-15px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'white',
                border: '1px solid #e1e1e1',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
              }}
              aria-label="Next slide"
            >
              ›
            </button>
          </div>
        </div>

        {/* Core Features Section */}
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px', color: '#008060' }}>⚡</div>
                <Heading>Key Features Overview</Heading>
                <Text as="p" color="subdued" style={{ margin: '15px 0 25px' }}>
                  Powerful tools designed specifically for Shopify store owners
                </Text>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: '20px',
                  marginTop: '20px'
                }}>
                  {/* Price Tools */}
                  <div style={{ padding: '20px', borderRadius: '8px', border: '1px solid #e6e6e6' }}>
                    <div style={{ fontSize: '32px', marginBottom: '15px' }}>💰</div>
                    <Text variant="headingMd" as="h3">Price Tools</Text>
                    <div style={{ marginTop: '15px' }}>
                      <Stack vertical spacing="tight">
                        {["Bulk Price Updates", "Price Rounding", "Compare-at-Price Management"].map((feature, i) => (
                          <Stack alignment="center" key={i}>
                            <div style={{ color: '#008060' }}>
                              <Icon source={CircleTickMajor} color="success" />
                            </div>
                            <Text variant="bodyMd" as="span">{feature}</Text>
                          </Stack>
                        ))}
                      </Stack>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                      <Button onClick={() => setPriceToolsModalOpen(true)}>Learn More</Button>
                    </div>
                  </div>

                  {/* Inventory Tools */}
                  <div style={{ padding: '20px', borderRadius: '8px', border: '1px solid #e6e6e6' }}>
                    <div style={{ fontSize: '32px', marginBottom: '15px' }}>📦</div>
                    <Text variant="headingMd" as="h3">Inventory Tools</Text>
                    <div style={{ marginTop: '15px' }}>
                      <Stack vertical spacing="tight">
                        {["Bulk Inventory Updates", "Selective Updates", "Save & Load Settings"].map((feature, i) => (
                          <Stack alignment="center" key={i}>
                            <div style={{ color: '#008060' }}>
                              <Icon source={CircleTickMajor} color="success" />
                            </div>
                            <Text variant="bodyMd" as="span">{feature}</Text>
                          </Stack>
                        ))}
                      </Stack>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                      <Button onClick={() => setInventoryToolsModalOpen(true)}>Learn More</Button>
                    </div>
                  </div>

                  {/* Product Tools */}
                  <div style={{ padding: '20px', borderRadius: '8px', border: '1px solid #e6e6e6' }}>
                    <div style={{ fontSize: '32px', marginBottom: '15px' }}>🏷️</div>
                    <Text variant="headingMd" as="h3">Product Tools</Text>
                    <div style={{ marginTop: '15px' }}>
                      <Stack vertical spacing="tight">
                        {["Handle Management", "Vendor Management", "SKU Generator"].map((feature, i) => (
                          <Stack alignment="center" key={i}>
                            <div style={{ color: '#008060' }}>
                              <Icon source={CircleTickMajor} color="success" />
                            </div>
                            <Text variant="bodyMd" as="span">{feature}</Text>
                          </Stack>
                        ))}
                      </Stack>
                    </div>
                    <div style={{ marginTop: '20px' }}>
                      <Button onClick={() => setProductToolsModalOpen(true)}>Learn More</Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </Layout.Section>
        </Layout>

        {/* CTA Section */}
        <div style={{ 
          marginTop: '50px', 
          padding: '40px 20px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #f9f9ff 0%, #f0f9ff 100%)',
          borderRadius: '8px'
        }}>
          <Heading>Bring Your Shopify Products to Life</Heading>
          <Text as="p" style={{ margin: '15px auto 30px', maxWidth: '650px' }}>
            Powered by Multiple AI Engines and Fully Customizable Templates
          </Text>
          <Button size="large" primary onClick={handleLoginClick}>Get Started Now</Button>
        </div>

        {/* Animation keyframes */}
        <style jsx>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Price Tools Modal */}
        <Modal
          open={priceToolsModalOpen}
          onClose={() => setPriceToolsModalOpen(false)}
          title="Price Tools"
          large
        >
          <Modal.Section>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px' }}>
                Bulk Price Updates - Modify prices across multiple products at once using percentage or fixed amount adjustments
              </p>
              <p style={{ marginBottom: '15px' }}>
                Price Change Types - Increase, decrease, or set specific prices with flexible options
              </p>
              <p style={{ marginBottom: '15px' }}>
                Compare-at-Price Management - Easily set and adjust compare-at prices to show savings
              </p>
              <p style={{ marginBottom: '15px' }}>
                Price Rounding - Automatically round prices to maintain consistent pricing patterns
              </p>
              <p style={{ marginBottom: '15px' }}>
                Save & Load Settings - Store pricing strategies for future use
              </p>
            </div>

            <div style={{ backgroundColor: '#f9fafb', padding: '15px', borderRadius: '5px' }}>
              <Stack vertical spacing="tight">
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Bulk Price Updates</strong> - Modify prices across multiple products at once</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Compare-at Pricing</strong> - Easily set and adjust compare-at prices</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Price Rounding</strong> - Automatically round prices to maintain consistent pricing patterns</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Save & Load Settings</strong> - Store your pricing strategies for future use</Text>
                </Stack>
              </Stack>
            </div>
          </Modal.Section>
        </Modal>

        {/* Inventory Tools Modal */}
        <Modal
          open={inventoryToolsModalOpen}
          onClose={() => setInventoryToolsModalOpen(false)}
          title="Inventory Tools"
          large
        >
          <Modal.Section>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px' }}>
                Bulk Inventory Updates - Update inventory quantities across multiple products simultaneously
              </p>
              <p style={{ marginBottom: '15px' }}>
                Selective Updates - Option to ignore products with zero inventory
              </p>
              <p style={{ marginBottom: '15px' }}>
                Save & Load Settings - Store inventory management preferences
              </p>
            </div>

            <div style={{ backgroundColor: '#f9fafb', padding: '15px', borderRadius: '5px' }}>
              <Stack vertical spacing="tight">
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Bulk Inventory Updates</strong> - Update inventory quantities across multiple products simultaneously</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Selective Updates</strong> - Choose which products to update based on specific criteria</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Zero Inventory Filter</strong> - Option to ignore products with zero inventory</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Save & Load Settings</strong> - Store your inventory management preferences</Text>
                </Stack>
              </Stack>
            </div>
          </Modal.Section>
        </Modal>

        {/* Product Tools Modal */}
        <Modal
          open={productToolsModalOpen}
          onClose={() => setProductToolsModalOpen(false)}
          title="Product Tools"
          large
        >
          <Modal.Section>
            <div style={{ marginBottom: '20px' }}>
              <p style={{ marginBottom: '15px' }}>
                Handle Management - Generate custom handles with prefixes/suffixes or empty handles for Shopify auto-generation
              </p>
              <p style={{ marginBottom: '15px' }}>
                Vendor Management - Update vendor information across multiple products
              </p>
              <p style={{ marginBottom: '15px' }}>
                SKU Generator - Create SKUs for all variants using customizable format with product title, variant options, and prefixes/suffixes
              </p>
              <p style={{ marginBottom: '15px' }}>
                Save & Load Settings - Store product management preferences for future use
              </p>
            </div>

            <div style={{ backgroundColor: '#f9fafb', padding: '15px', borderRadius: '5px' }}>
              <Stack vertical spacing="tight">
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Handle Management</strong> - Generate custom handles with prefixes/suffixes or empty handles for Shopify auto-generation</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Vendor Management</strong> - Update vendor information across multiple products</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>SKU Generator</strong> - Create SKUs for all variants using customizable format</Text>
                </Stack>
                <Stack alignment="center">
                  <div style={{ color: '#008060' }}>
                    <Icon source={CircleTickMajor} color="success" />
                  </div>
                  <Text variant="bodyMd" as="span"><strong>Save & Load Settings</strong> - Store product management preferences for future use</Text>
                </Stack>
              </Stack>
            </div>
          </Modal.Section>
        </Modal>

        {/* Trust Section */}
        <div style={{ padding: '60px 20px', background: '#f9fafb', marginTop: '60px' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <Heading element="h2">Your Data & Payments Are Safe With Us</Heading>
              <Text variant="bodyLg" as="p" color="subdued">
                We prioritize security, transparency, and reliability
              </Text>
            </div>
            
            {/* Section divider - subtle visual anchor */}
            <div style={{ 
              width: '60px', 
              height: '4px', 
              background: 'linear-gradient(90deg, rgba(0,128,96,0.2) 0%, rgba(0,128,96,0.8) 50%, rgba(0,128,96,0.2) 100%)', 
              margin: '-10px auto 40px',
              borderRadius: '2px'
            }} />
            
            {/* Trust points in a grid - Desktop view */}
            <div className="desktop-trust-cards" style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '30px',
              justifyContent: 'center',
              marginBottom: '40px'
            }}>
              {/* Each card will be wrapped in a div with fixed width */}

              {/* Trust Point 1 - Secure Payments */}
              <div style={{ width: 'calc(50% - 15px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>💳</div>
                  <Text variant="headingSm" as="h3">Secure Payments</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      All payments are processed via encrypted gateways using Stripe or PayPal.
                    </Text>
                  </div>
                </div>
              </div>
              
              {/* Trust Point 2 - Data Privacy */}
              <div style={{ width: 'calc(50% - 15px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>🔒</div>
                  <Text variant="headingSm" as="h3">Data Privacy First</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Your product data is never shared or used to train external AI models.
                    </Text>
                  </div>
                </div>
              </div>

              {/* Trust Point 3 - Trusted AI Providers */}
              <div style={{ width: 'calc(50% - 15px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>💼</div>
                  <Text variant="headingSm" as="h3">Trusted AI Providers</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Powered by industry-leading AI providers: OpenAI, Google, DeepSeek.
                    </Text>
                  </div>
                </div>
              </div>

              {/* Trust Point 4 - Transparent Pricing */}
              <div style={{ width: 'calc(50% - 15px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>📊</div>
                  <Text variant="headingSm" as="h3">Transparent Pricing</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      No hidden costs. You only pay for what you use. Credits never expire.
                    </Text>
                  </div>
                </div>
              </div>

              {/* Trust Point 5 - Flexible Credit System */}
              <div style={{ width: 'calc(50% - 15px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>♻️</div>
                  <Text variant="headingSm" as="h3">Flexible Credit System</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Use credits across features and upgrade anytime without commitment.
                    </Text>
                  </div>
                </div>
              </div>

              {/* Trust Point 6 - Global Infrastructure */}
              <div style={{ width: 'calc(50% - 15px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>🌎</div>
                  <Text variant="headingSm" as="h3">Global Infrastructure</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Fast and reliable, whether you're selling in the US, Europe, or worldwide.
                    </Text>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile slider for Trust Cards */}
            <div className="mobile-trust-slider" style={{
              display: 'none',
              position: 'relative',
              maxWidth: '280px',
              margin: '30px auto 40px'
            }}>
              <div style={{
                overflow: 'hidden',
                position: 'relative',
                borderRadius: '10px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div className="slider-container" style={{
                  display: 'flex',
                  transition: 'transform 0.3s ease-in-out',
                  transform: `translateX(-${currentTrustSlide * 100}%)`
                }}>
                  {/* Slide 1 - Secure Payments */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>💳</div>
                    <Text variant="headingSm" as="h3">Secure Payments</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      All payments are processed via encrypted gateways using Stripe or PayPal.
                    </Text>
                  </div>
                  
                  {/* Slide 2 - Data Privacy */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>🔒</div>
                    <Text variant="headingSm" as="h3">Data Privacy First</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Your product data is never shared or used to train external AI models.
                    </Text>
                  </div>
                  
                  {/* Slide 3 - Trusted AI Providers */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>💼</div>
                    <Text variant="headingSm" as="h3">Trusted AI Providers</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Powered by industry-leading AI providers: OpenAI, Google, DeepSeek.
                    </Text>
                  </div>
                  
                  {/* Slide 4 - Transparent Pricing */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>📊</div>
                    <Text variant="headingSm" as="h3">Transparent Pricing</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      No hidden costs. You only pay for what you use. Credits never expire.
                    </Text>
                  </div>
                  
                  {/* Slide 5 - Flexible Credit System */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>♻️</div>
                    <Text variant="headingSm" as="h3">Flexible Credit System</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Use credits across features and upgrade anytime without commitment.
                    </Text>
                  </div>
                  
                  {/* Slide 6 - Global Infrastructure */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>🌎</div>
                    <Text variant="headingSm" as="h3">Global Infrastructure</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Fast and reliable, whether you're selling in the US, Europe, or worldwide.
                    </Text>
                  </div>
                </div>
              </div>
              
              {/* Slider dots */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '15px',
                gap: '8px'
              }}>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <button 
                    key={index}
                    onClick={() => setCurrentTrustSlide(index)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: currentTrustSlide === index ? '#008060' : '#e1e1e1',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    aria-label={`Go to trust slide ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Left/Right navigation arrows */}
              <button 
                onClick={() => setCurrentTrustSlide(prev => (prev === 0 ? 5 : prev - 1))}
                style={{
                  position: 'absolute',
                  left: '-15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  border: '1px solid #e1e1e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
                aria-label="Previous trust slide"
              >
                ‹
              </button>
              <button 
                onClick={() => setCurrentTrustSlide(prev => (prev === 5 ? 0 : prev + 1))}
                style={{
                  position: 'absolute',
                  right: '-15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  border: '1px solid #e1e1e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
                aria-label="Next trust slide"
              >
                ›
              </button>
            </div>
          </div>
        </div>

        {/* AI Template System Section */}
        <div style={{ padding: '60px 20px', background: 'linear-gradient(135deg, #f5f9ff 0%, #eefbf4 100%)' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>✨</div>
              <Heading>AI-Powered Template System</Heading>
              <Text variant="bodyLg" as="p" color="subdued" style={{ maxWidth: '700px', margin: '15px auto 40px' }}>
                Create compelling product content at scale with our flexible, user-driven template system
              </Text>
            </div>
            
            <div className="desktop-template-cards" style={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: '30px',
              justifyContent: 'center',
              marginBottom: '40px'
            }}>
              {/* Feature 1 */}
              <div style={{ width: 'calc(33.33% - 20px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>📝</div>
                  <Text variant="headingSm" as="h3">User-Driven Input</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Select exactly which fields you want as input - both standard fields and custom metafields
                    </Text>
                  </div>
                </div>
              </div>
              
              {/* Feature 2 */}
              <div style={{ width: 'calc(33.33% - 20px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>🤖</div>
                  <Text variant="headingSm" as="h3">Choose Your AI Engine</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Select from multiple AI models with transparent credit costs for each template execution
                    </Text>
                  </div>
                </div>
              </div>
              
              {/* Feature 3 */}
              <div style={{ width: 'calc(33.33% - 20px)', minWidth: '280px' }}>
                <div style={{ 
                  background: '#ffffff', 
                  borderRadius: '10px', 
                  padding: '30px', 
                  boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  height: '100%'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 128, 96, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.05)';
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '15px' }}>🔄</div>
                  <Text variant="headingSm" as="h3">Flexible Output Targeting</Text>
                  <div style={{ marginTop: '10px' }}>
                    <Text variant="bodyMd" as="p" color="subdued">
                      Control exactly where AI-generated content goes - standard fields or custom metafields
                    </Text>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile slider for AI Template System */}
            <div className="mobile-template-slider" style={{
              display: 'none',
              position: 'relative',
              maxWidth: '280px',
              margin: '30px auto 40px'
            }}>
              <div style={{
                overflow: 'hidden',
                position: 'relative',
                borderRadius: '10px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <div className="slider-container" style={{
                  display: 'flex',
                  transition: 'transform 0.3s ease-in-out',
                  transform: `translateX(-${currentTemplateSlide * 100}%)`
                }}>
                  {/* Slide 1 - User-Driven Input */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>📝</div>
                    <Text variant="headingSm" as="h3">User-Driven Input</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Select exactly which fields you want as input - both standard fields and custom metafields
                    </Text>
                  </div>
                  
                  {/* Slide 2 - Choose Your AI Engine */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>🤖</div>
                    <Text variant="headingSm" as="h3">Choose Your AI Engine</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Select from multiple AI models with transparent credit costs for each template execution
                    </Text>
                  </div>
                  
                  {/* Slide 3 - Flexible Output Targeting */}
                  <div style={{
                    minWidth: '100%',
                    background: '#ffffff',
                    padding: '35px 20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                  }}>
                    <div style={{ fontSize: '36px', marginBottom: '16px' }}>🔄</div>
                    <Text variant="headingSm" as="h3">Flexible Output Targeting</Text>
                    <Text variant="bodyMd" as="p" color="subdued" style={{ marginTop: '10px', textAlign: 'center' }}>
                      Control exactly where AI-generated content goes - standard fields or custom metafields
                    </Text>
                  </div>
                </div>
              </div>
              
              {/* Slider dots */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '15px',
                gap: '8px'
              }}>
                {[0, 1, 2].map((index) => (
                  <button 
                    key={index}
                    onClick={() => setCurrentTemplateSlide(index)}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      border: 'none',
                      backgroundColor: currentTemplateSlide === index ? '#008060' : '#e1e1e1',
                      cursor: 'pointer',
                      padding: 0
                    }}
                    aria-label={`Go to template slide ${index + 1}`}
                  />
                ))}
              </div>
              
              {/* Left/Right navigation arrows */}
              <button 
                onClick={() => setCurrentTemplateSlide(prev => (prev === 0 ? 2 : prev - 1))}
                style={{
                  position: 'absolute',
                  left: '-15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  border: '1px solid #e1e1e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
                aria-label="Previous template slide"
              >
                ‹
              </button>
              <button 
                onClick={() => setCurrentTemplateSlide(prev => (prev === 2 ? 0 : prev + 1))}
                style={{
                  position: 'absolute',
                  right: '-15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'white',
                  border: '1px solid #e1e1e1',
                  borderRadius: '50%',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
                aria-label="Next template slide"
              >
                ›
              </button>
            </div>
            
            <div style={{ textAlign: 'center' }}>
              <Button size="large" primary onClick={() => handleNavClick('/templates')}>
                Explore Templates
              </Button>
            </div>
          </div>
        </div>

        {/* Testimonials Section */}
        <div style={{ padding: '40px 20px', background: '#f9fafb', marginTop: '0' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <Heading element="h2">🛒 Join 1,500+ Shopify sellers using Editify</Heading>
              
              {/* Section divider - subtle visual anchor */}
              <div style={{ 
                width: '60px', 
                height: '4px', 
                background: 'linear-gradient(90deg, rgba(0,128,96,0.2) 0%, rgba(0,128,96,0.8) 50%, rgba(0,128,96,0.2) 100%)', 
                margin: '15px auto 30px',
                borderRadius: '2px'
              }} />
            </div>
            
            {/* Testimonial Slider */}
            <TestimonialSlider />
          </div>
        </div>
        
        {/* CTA Card */}
        <div style={{ padding: '40px 20px', background: '#f5f9f7' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div>
              <div 
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 128, 96, 0.05) 0%, rgba(255, 255, 255, 1) 100%)',
                  padding: '30px',
                  borderRadius: '10px',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.05)',
                  maxWidth: '600px',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,128,96,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.05)';
                }}
              >
                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                  <Text variant="headingLg" style={{ color: '#008060' }}>🤩 Start saving hours like our customers</Text>
                </div>
                <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                  <Text variant="bodyMd">Join 1,500+ Shopify merchants who are streamlining their product management</Text>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <Button size="large" primary onClick={handleLoginClick}>
                    Get Editify Now!
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Page>
    </Frame>
  );
};

export default Home;
